/**
 * Client-side PostHog analytics helpers
 *
 * Typed `track()` wrapper around posthog-js plus a single source of truth for
 * all client-side event name constants.  Keeps posthog-js at arm's length so
 * tests can mock this module rather than the SDK directly.
 *
 * @changeHistory
 * - 2026-10-01: Added broker/admin workspace view-change event (Codex)
 * - 2026-06-22: Initial creation - Typed event wrapper + CLIENT_EVENTS constants (GitHub Copilot)
 */

import posthog from 'posthog-js';

/** All PostHog event names used by the client — single source of truth. */
export const CLIENT_EVENTS = {
  // Navigation
  PAGE_VIEWED: 'agent_portal_page_viewed',
  WORKSPACE_VIEW_CHANGED: 'agent_portal_workspace_view_changed',

  // Auth
  LOGIN_STARTED: 'agent_portal_login_started',
  LOGIN_SUCCEEDED: 'agent_portal_login_succeeded',
  LOGIN_FAILED: 'agent_portal_login_failed',
  LOGOUT: 'agent_portal_logout',

  // CDA submission
  CDA_SUBMIT_STARTED: 'agent_portal_cda_submit_started',
  CDA_SUBMIT_COMPLETED: 'agent_portal_cda_submit_completed',
  CDA_SUBMIT_FAILED: 'agent_portal_cda_submit_failed',
  CDA_EDIT_STARTED: 'agent_portal_cda_edit_started',
  CDA_EDIT_COMPLETED: 'agent_portal_cda_edit_completed',
  CDA_EDIT_FAILED: 'agent_portal_cda_edit_failed',

  // Approval workflow
  CDA_APPROVE_STARTED: 'agent_portal_cda_approve_started',
  CDA_APPROVE_COMPLETED: 'agent_portal_cda_approve_completed',
  CDA_APPROVE_FAILED: 'agent_portal_cda_approve_failed',
  CDA_REJECT_STARTED: 'agent_portal_cda_reject_started',
  CDA_REJECT_COMPLETED: 'agent_portal_cda_reject_completed',
  CDA_REJECT_FAILED: 'agent_portal_cda_reject_failed',
  CDA_CANCEL_STARTED: 'agent_portal_cda_cancel_started',
  CDA_CANCEL_COMPLETED: 'agent_portal_cda_cancel_completed',
  CDA_CANCEL_FAILED: 'agent_portal_cda_cancel_failed',

  // Blog posts
  BLOG_POST_CREATE_STARTED: 'agent_portal_blog_post_create_started',
  BLOG_POST_CREATE_COMPLETED: 'agent_portal_blog_post_create_completed',
  BLOG_POST_CREATE_FAILED: 'agent_portal_blog_post_create_failed',
  BLOG_POST_EDIT_STARTED: 'agent_portal_blog_post_edit_started',
  BLOG_POST_EDIT_COMPLETED: 'agent_portal_blog_post_edit_completed',
  BLOG_POST_EDIT_FAILED: 'agent_portal_blog_post_edit_failed',
  BLOG_POST_DELETED: 'agent_portal_blog_post_deleted',

  // Profile
  PROFILE_EDIT_STARTED: 'agent_portal_profile_edit_started',
  PROFILE_EDIT_COMPLETED: 'agent_portal_profile_edit_completed',
  PROFILE_EDIT_FAILED: 'agent_portal_profile_edit_failed',

  // Agent management
  AGENT_EDIT_STARTED: 'agent_portal_agent_edit_started',
  AGENT_EDIT_COMPLETED: 'agent_portal_agent_edit_completed',
  AGENT_EDIT_FAILED: 'agent_portal_agent_edit_failed',

  // Team
  TEAM_MEMBER_EDIT_STARTED: 'agent_portal_team_member_edit_started',
  TEAM_MEMBER_EDIT_COMPLETED: 'agent_portal_team_member_edit_completed',
  TEAM_MEMBER_EDIT_FAILED: 'agent_portal_team_member_edit_failed',
} as const;

export type ClientEventName = (typeof CLIENT_EVENTS)[keyof typeof CLIENT_EVENTS];

type TrackProperties = Record<string, string | number | boolean | null | undefined>;
const isPostHogConfigured = Boolean(import.meta.env.VITE_PUBLIC_POSTHOG_KEY);

/**
 * Emit a PostHog client-side event.
 *
 * Safe no-op when PostHog was not initialised (key not configured).
 */
export function track(event: ClientEventName, properties?: TrackProperties): void {
  if (!isPostHogConfigured) {
    return;
  }

  posthog.capture(event, properties);
}
